import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Upload, Grid, List, Filter, Clock,
  Video, PlayCircle, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { explainerVideosAPI } from '../../services/api';
import { BASE_URL } from '../../constants/config';
import VideoPlayerModal from '../../components/common/VideoPlayerModal';
import './ExplainerVideos.css';
import { formatDate } from '../../utils/dateFormatter';

// Placeholder image URL
const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;

const ExplainerVideos = () => {
  const navigate = useNavigate();
  const { openVideoUploadModal, currentUser } = useAppContext();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const { filteredExplainerVideos, canUploadExplainerVideo, refreshData, loading } = useRoleData();
  const [fetchedCategories, setFetchedCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await explainerVideosAPI.getCategories();
        setFetchedCategories(response.data.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Refresh video list when new video is uploaded
  useEffect(() => {
    const handleVideoUploaded = () => {
      refreshData();
    };
    window.addEventListener('videoUploaded', handleVideoUploaded);
    return () => window.removeEventListener('videoUploaded', handleVideoUploaded);
  }, [refreshData]);

  const categories = ['All', ...fetchedCategories];
  console.log('Filters:', { search, selectedCategory, statusFilter });

  const filtered = filteredExplainerVideos.filter((v) => {
    try {
      const matchSearch =
        v.title.toLowerCase().includes(search.toLowerCase()) ||
        v.description.toLowerCase().includes(search.toLowerCase()) ||
        (Array.isArray(v.tags) && v.tags.some((t) => t.toLowerCase().includes(search.toLowerCase())));
      const matchCategory = selectedCategory === 'All' || v.category === selectedCategory;
      const matchStatus = statusFilter === 'All' || v.status === statusFilter;
      return matchSearch && matchCategory && matchStatus;
    } catch (err) {
      console.error('Error filtering video:', v, err);
      return false;
    }
  });

  console.log('filteredExplainerVideos:', filteredExplainerVideos, 'filtered:', filtered);
  if (filteredExplainerVideos.length > 0) console.log('First video keys:', Object.keys(filteredExplainerVideos[0]));


  const openVideoPlayerModal = (video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const closeVideoPlayerModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="explainer-videos">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>
            {currentUser?.role === 'Trainee' ? 'Available Videos' : 'Explainer Videos'}
          </h1>
          <p>{filteredExplainerVideos.length} explainer videos available</p>
        </div>
        {canUploadExplainerVideo && (
          <button className="btn btn-primary" onClick={openVideoUploadModal}>
            <Upload size={17} />
            Upload Video
          </button>
        )}
      </div>

      {/* Filters Bar */}
      <div className="explainer-videos__filters card">
        <div className="explainer-videos__search">
          <Search size={16} className="explainer-videos__search-icon" />
          <input
            className="explainer-videos__search-input"
            type="text"
            placeholder="Search videos by title, tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="explainer-videos__filter-group">
          <Filter size={14} />
          <select
            className="explainer-videos__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Draft">Draft</option>
          </select>
        </div>

        <div className="explainer-videos__view-toggle">
          <button
            className={`explainer-videos__view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={16} />
          </button>
          <button
            className={`explainer-videos__view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={16} />
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="explainer-videos__categories">
        {categories.slice(0, 7).map((cat) => (
          <button
            key={cat}
            className={`explainer-videos__cat-btn ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
            {cat !== 'All' && (
              <span className="explainer-videos__cat-count">
                {filteredExplainerVideos.filter((v) => v.category === cat).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Results Count */}
      {!loading && (
        <div className="explainer-videos__results-info">
          <span>Showing <strong>{filtered.length}</strong> of {filteredExplainerVideos.length} items</span>
        </div>
      )}

      {/* Videos Grid / List */}
      {loading ? (
        <div className="explainer-videos__loading">
          <div className="explainer-videos__spinner"></div>
          <p>Loading videos...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Search size={28} /></div>
          <h3>No videos found</h3>
          <p>
            {currentUser?.role === 'Trainee' ? 'You are not enrolled in any modules with explainer videos.' :
             'Try adjusting your search or filters'}
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="explainer-videos__grid">
          {filtered.map((video) => (
            <div
              key={video.id}
              className="explainer-videos__card card"
              onClick={() => navigate(`/explainer-videos/${video.videoId ? video.videoId.replace(/-/g, '') : video.id}`, { state: { fromList: true } })}
            >
              <div className="explainer-videos__card-thumb">
                <img src={video.thumbnail || PLACEHOLDER_IMAGE} alt={video.title} />
                <span className={`explainer-videos__status-dot ${video.status === 'Active' ? 'active' : 'draft'}`} />
                <div
                  className="explainer-videos__card-play"
                  onClick={(e) => {
                    e.stopPropagation();
                    openVideoPlayerModal(video);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      openVideoPlayerModal(video);
                    }
                  }}
                >
                  <PlayCircle size={32} />
                </div>
              </div>
              <div className="explainer-videos__card-body">
                <div className="explainer-videos__card-top">
                  <span className="badge badge-green">{video.category}</span>
                  <span className="badge badge-dark">{video.videoType}</span>
                </div>
                <h3 className="explainer-videos__card-title">{video.title}</h3>
                <p className="explainer-videos__card-desc">{video.description}</p>
                <div className="explainer-videos__card-tags">
                  {video.tags.slice(0, 3).map((t) => (
                    <span key={t} className="explainer-videos__tag">#{t}</span>
                  ))}
                </div>
                <div className="explainer-videos__card-footer">
                  <span><Clock size={12} /> {formatDate(video.uploadDate)}</span>
                  <span><Video size={12} /> {video.videoType}</span>
                </div>
                <div className="explainer-videos__card-uploader">
                  <span>Uploaded by {video.uploadedByName}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="explainer-videos__list">
            {filtered.map((video, idx) => (
              <div
                key={video.id}
                className={`explainer-videos__list-item ${idx < filtered.length - 1 ? 'explainer-videos__list-item--bordered' : ''}`}
                onClick={() => navigate(`/explainer-videos/${video.videoId ? video.videoId.replace(/-/g, '') : video.id}`, { state: { fromList: true } })}
              >
                <div className="explainer-videos__list-thumb">
                  <img src={video.thumbnail || PLACEHOLDER_IMAGE} alt={video.title} />
                  <div
                    className="explainer-videos__list-play"
                    onClick={(e) => {
                      e.stopPropagation();
                      openVideoPlayerModal(video);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        openVideoPlayerModal(video);
                      }
                    }}
                  >
                    <PlayCircle size={20} />
                  </div>
                </div>
                <div className="explainer-videos__list-info">
                  <span className="explainer-videos__list-title">{video.title}</span>
                  <div className="explainer-videos__list-meta">
                    <span className="badge badge-green">{video.category}</span>
                    <span><Clock size={11} /> {video.uploadDate}</span>
                    <span><Video size={11} /> {video.videoType}</span>
                    <span>Uploaded by {video.uploadedByName}</span>
                  </div>
                </div>
                <div className="explainer-videos__list-stats">
                  <span className={`badge ${video.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                    {video.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <VideoPlayerModal
        video={selectedVideo}
        isOpen={isVideoModalOpen}
        onClose={closeVideoPlayerModal}
      />
    </div>
  );
};

export default ExplainerVideos;